import React from "react";
import { SenderDetailsView } from "@tiny/api";
import { useGetSenderDetailsQuery } from "@tiny/api";
import { AutocompleteField } from "@tiny/components";
import useDebounce from "@tiny/components/src/hooks/useDebounce";

export interface SenderDetailsInputProps {
  value?: string[];
  onChangeValue?: (value: string[]) => void;
  helperText?: string;
  error?: boolean;
  inputRef?: React.ForwardedRef<HTMLInputElement>;
  disabled?: boolean;
  multiple?: boolean;
}

export function SenderDetailsInput(props: SenderDetailsInputProps) {
  const {
    value,
    onChangeValue,
    helperText,
    error,
    inputRef,
    disabled,
    multiple = true,
  } = props;

  const [internalSenderValue, setInternalSenderValue] =
    React.useState<SenderDetailsView[]>();

  const [search, setSearch] = React.useState("");

  const searchDebounced = useDebounce(search, 300);

  const {
    data: senderDetailsData,
    isLoading,
    isError,
  } = useGetSenderDetailsQuery({
    enabled: true,
  });

  const allSenders = senderDetailsData?.senderDetailses || [];

  const searchResult = React.useMemo(() => {
    if (!searchDebounced) return allSenders;
    const searchLower = searchDebounced.toLowerCase();
    return allSenders.filter(
      (sender) =>
        sender.name?.toLowerCase().includes(searchLower) ||
        sender.email?.toLowerCase().includes(searchLower)
    );
  }, [allSenders, searchDebounced]);

  React.useEffect(() => {
    if (!value) return;

    const selected = allSenders.filter((sender) =>
      value.includes(sender.email!)
    );

    setInternalSenderValue(selected);
  }, [value, allSenders]);

  const changeInput = (_event: React.ChangeEvent<{}>, v: string) => {
    setSearch(v);
  };

  const changeValue = (v: SenderDetailsView | SenderDetailsView[]) => {
    if (multiple) {
      setInternalSenderValue(v as SenderDetailsView[]);
      onChangeValue?.((v as SenderDetailsView[]).map((s) => s.email!));
    } else {
      setInternalSenderValue([v as SenderDetailsView]);
      onChangeValue?.([(v as SenderDetailsView).email!]);
    }
  };

  const hasNoSenders = !isLoading && !isError && allSenders.length === 0;

  if (hasNoSenders) {
    return (
      <AutocompleteField
        options={[]}
        value={multiple ? [] : null}
        multiple={multiple}
        placeholder="No senders configured"
        helperText={helperText}
        error={error}
        showHelperTextMargin
        disabled
      />
    );
  }

  return (
    <AutocompleteField
      inputRef={inputRef}
      multiple={multiple}
      loading={isLoading}
      options={searchResult}
      inputValue={search}
      value={
        multiple ? internalSenderValue || [] : internalSenderValue?.[0] || null
      }
      onInputChange={changeInput}
      onChangeValue={changeValue}
      getOptionLabel={(option) => `${option?.name} (${option?.email})`}
      limitTags={1}
      helperText={helperText}
      error={error}
      showHelperTextMargin
      disabled={disabled}
      getOptionSelected={(option, value) => option.email === value.email}
    />
  );
}
